import { Link, useParams } from "react-router-dom";
import Breadcrumb from "@/components/base/Breadcrumb";
import MediaCard from "@/components/feature/MediaCard";
import SectionHeading from "@/components/base/SectionHeading";
import { getMediaById, getRelatedMedia } from "@/lib/media";
import { useFavorites } from "@/lib/favorites";
import { usePageMeta } from "@/hooks/usePageMeta";

export default function MediaDetail() {
  const { id } = useParams();
  const item = getMediaById(id);
  const { isFavorite, toggleFavorite } = useFavorites();

  usePageMeta({
    title: item ? `${item.title}-555电影网` : "影视详情-555电影网",
    description: item ? item.synopsis.slice(0, 150) : undefined,
    keywords: item ? `${item.title},${item.genres.join(",")},555电影网` : undefined,
  });

  if (!item) {
    return (
      <div className="mx-auto flex max-w-page flex-col items-center px-4 py-24 md:px-6">
        <div className="flex h-16 w-16 items-center justify-center text-foreground-300">
          <i className="ri-emotion-sad-line text-4xl"></i>
        </div>
        <p className="mt-4 text-foreground-600">抱歉，没有找到该影视作品</p>
        <Link
          to="/"
          className="mt-5 whitespace-nowrap rounded-md bg-secondary-500 px-5 py-2 text-sm text-background-50 transition-colors hover:bg-secondary-600"
        >
          返回首页
        </Link>
      </div>
    );
  }

  const related = getRelatedMedia(item, 5);
  const categoryPath = item.type === "movie" ? "/category/movie" : "/category/tv";
  const categoryLabel = item.type === "movie" ? "电影" : "电视剧";
  const favorited = isFavorite(item.id);

  const metaRows = [
    { label: "导演", value: item.director },
    { label: "主演", value: item.actors.join("、") },
    { label: "类型", value: item.genres.join(" / ") },
    { label: item.type === "movie" ? "上映年份" : "首播年份", value: `${item.year}` },
    { label: "地区", value: item.region },
    {
      label: item.type === "movie" ? "片长" : "集数",
      value:
        item.type === "movie"
          ? item.duration
          : `${item.duration} · 全${item.episodes ?? "?"}集`,
    },
  ];

  return (
    <div className="mx-auto max-w-page px-4 py-6 md:px-6 md:py-8">
      <Breadcrumb
        items={[
          { label: "首页", to: "/" },
          { label: categoryLabel, to: categoryPath },
          { label: item.title },
        ]}
      />

      <div className="mt-5 overflow-hidden rounded-xl border border-background-200 bg-background-50">
        <div className="grid grid-cols-1 gap-6 md:grid-cols-[240px_1fr]">
          <div className="relative h-64 w-full overflow-hidden bg-background-200 md:h-full md:min-h-[360px]">
            <img
              src={item.poster}
              alt={`${item.title} 海报`}
              title={`${item.title} - 555电影网`}
              className="h-full w-full object-top"
            />
          </div>

          <div className="p-5 md:p-7">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <h1 className="font-heading text-2xl font-bold text-foreground-900 md:text-3xl">
                    {item.title}
                  </h1>
                  <span className="rounded bg-primary-100 px-2 py-0.5 text-xs font-medium text-primary-700">
                    {categoryLabel}
                  </span>
                </div>
                <p className="mt-2 text-sm text-foreground-500">{item.tagline}</p>
              </div>
              <button
                type="button"
                onClick={() => toggleFavorite(item.id)}
                className={`flex items-center gap-1.5 whitespace-nowrap rounded-md border px-4 py-2 text-sm font-medium transition-colors ${
                  favorited
                    ? "border-accent-500 bg-accent-50 text-accent-700"
                    : "border-background-300 text-foreground-600 hover:border-accent-300 hover:text-accent-600"
                }`}
              >
                <i className={favorited ? "ri-heart-3-fill" : "ri-heart-3-line"}></i>
                {favorited ? "已收藏" : "加入收藏"}
              </button>
            </div>

            <div className="mt-4 flex flex-wrap items-center gap-4">
              <div className="flex items-baseline gap-1 text-accent-600">
                <i className="ri-star-fill text-lg"></i>
                <span className="font-heading text-2xl font-bold">{item.rating.toFixed(1)}</span>
                <span className="text-xs text-foreground-400">/ 10.0</span>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {item.genres.map((genre) => (
                  <span
                    key={genre}
                    className="rounded bg-secondary-50 px-2 py-0.5 text-xs text-secondary-700"
                  >
                    {genre}
                  </span>
                ))}
              </div>
            </div>

            <dl className="mt-5 grid grid-cols-1 gap-x-8 gap-y-2.5 border-t border-background-200 pt-5 sm:grid-cols-2">
              {metaRows.map((row) => (
                <div key={row.label} className="flex gap-2 text-sm">
                  <dt className="w-16 shrink-0 text-foreground-400">{row.label}</dt>
                  <dd className="min-w-0 flex-1 text-foreground-700">{row.value}</dd>
                </div>
              ))}
            </dl>

            <div className="mt-5 flex flex-wrap gap-3">
              <Link
                to={categoryPath}
                className="flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-700"
              >
                <i className="ri-folder-2-line"></i>
                浏览更多{categoryLabel}
              </Link>
              <span className="flex items-center gap-1.5 rounded-md bg-background-100 px-4 py-2 text-xs text-foreground-500">
                <i className="ri-information-line"></i>
                本站仅提供内容介绍，不提供真实播放与下载
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-8 grid grid-cols-1 gap-8 lg:grid-cols-[1fr_320px]">
        <div className="min-w-0">
          <section className="rounded-lg border border-background-200 bg-background-50 p-6">
            <div className="flex items-center gap-2">
              <span className="h-5 w-1 rounded-full bg-accent-500"></span>
              <h2 className="font-heading text-lg font-bold text-foreground-900">剧情简介</h2>
            </div>
            <p className="mt-4 leading-relaxed text-foreground-700">{item.synopsis}</p>
            <div className="mt-4 flex flex-wrap gap-2">
              {item.tags.map((tag) => (
                <span
                  key={tag}
                  className="rounded-full bg-background-100 px-3 py-1 text-xs text-foreground-600"
                >
                  #{tag}
                </span>
              ))}
            </div>
          </section>

          <section className="mt-6 rounded-lg border border-background-200 bg-background-50 p-6">
            <div className="flex items-center gap-2">
              <span className="h-5 w-1 rounded-full bg-secondary-500"></span>
              <h2 className="font-heading text-lg font-bold text-foreground-900">评分示意</h2>
            </div>
            <p className="mt-2 text-xs text-foreground-400">
              以下评分与用户评价均为页面演示数据，非真实评分。
            </p>
            <div className="mt-4 space-y-4">
              <div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-foreground-600">站内综合评分</span>
                  <span className="font-medium text-accent-600">{item.rating.toFixed(1)} 分</span>
                </div>
                <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-background-200">
                  <div
                    className="h-full rounded-full bg-accent-500"
                    style={{ width: `${item.rating * 10}%` }}
                  ></div>
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-foreground-600">观众口碑（示意）</span>
                  <span className="font-medium text-secondary-600">
                    {(item.rating - 0.3).toFixed(1)} 分
                  </span>
                </div>
                <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-background-200">
                  <div
                    className="h-full rounded-full bg-secondary-500"
                    style={{ width: `${(item.rating - 0.3) * 10}%` }}
                  ></div>
                </div>
              </div>
            </div>
          </section>
        </div>

        <aside>
          <div className="rounded-lg border border-background-200 bg-background-50 p-5">
            <h2 className="border-b border-background-200 pb-3 font-heading text-base font-bold text-foreground-900">
              作品信息
            </h2>
            <ul className="mt-3 space-y-3 text-sm">
              <li className="flex items-center justify-between">
                <span className="text-foreground-400">分类</span>
                <Link to={categoryPath} className="text-secondary-600 hover:underline">
                  {categoryLabel}
                </Link>
              </li>
              <li className="flex items-center justify-between">
                <span className="text-foreground-400">年份</span>
                <span className="text-foreground-700">{item.year}</span>
              </li>
              <li className="flex items-center justify-between">
                <span className="text-foreground-400">地区</span>
                <span className="text-foreground-700">{item.region}</span>
              </li>
              <li className="flex items-center justify-between">
                <span className="text-foreground-400">评分</span>
                <span className="text-accent-600">{item.rating.toFixed(1)}</span>
              </li>
            </ul>
          </div>
        </aside>
      </div>

      {related.length > 0 && (
        <section className="mt-10">
          <SectionHeading
            title="相关推荐"
            subtitle="根据类型与标签为你推荐相似作品"
            to={categoryPath}
          />
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
            {related.map((relatedItem) => (
              <MediaCard key={relatedItem.id} item={relatedItem} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}